<script setup>
import { computed, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';

import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';

/**
 * LaisMoneySnapshot — canonical monetary read-out for every LAIS domain.
 *
 * A money value is never a bare number: this component always renders the
 * currency, an optional unit (e.g. "/mês", "/m²") and the provenance line
 * (source + snapshot date). When provenance is missing it shows a neutral
 * "sem origem" chip instead of hiding the fact, and a null/undefined amount
 * degrades to an em dash with a helper — it never silently disappears.
 */
const props = defineProps({
  amount: { type: [Number, String], default: null },
  currency: { type: String, default: 'BRL' },
  // muted suffix appended after the value, e.g. "/mês" or "/m²"
  unit: { type: String, default: '' },
  label: { type: String, default: '' },
  // where the value came from (shown verbatim in the provenance line)
  source: { type: String, default: '' },
  snapshotAt: { type: [String, Date], default: '' },
  confidence: {
    type: [String, Number],
    default: '',
    validator: value => {
      if (value === '' || value === null || value === undefined) return true;
      if (typeof value === 'number') return value >= 0 && value <= 1;
      return ['low', 'medium', 'high'].includes(String(value).toLowerCase());
    },
  },
  // optional FX context: { from, to, value }
  rate: { type: Object, default: null },
  // optional compact trail: [{ at, amount }]
  history: { type: Array, default: () => [] },
  emphasis: {
    type: String,
    default: 'md',
    validator: value => ['lg', 'md', 'sm'].includes(value),
  },
  // tints the value only; unknown tones fall back to neutral
  // Accept any string: unknown tones map to neutral via LaisStatusBadge. A
  // strict enum validator would warn on values the badge already handles.
  tone: { type: String, default: 'neutral' },
  copyable: { type: Boolean, default: false },
});

const emit = defineEmits(['copy']);

const TONE_ALIASES = Object.freeze({
  neutral: 'neutral',
  unknown: 'neutral',
  info: 'info',
  active: 'info',
  positive: 'positive',
  success: 'positive',
  warning: 'warning',
  pending: 'warning',
  danger: 'danger',
  error: 'danger',
});

const VALUE_TONE_CLASSES = Object.freeze({
  neutral: 'text-lais-text-strong',
  info: 'text-lais-primary',
  positive: 'text-lais-mint-dark',
  warning: 'text-lais-text',
  danger: 'text-lais-danger',
});

const EMPHASIS_CLASSES = Object.freeze({
  lg: 'text-lais-display-sm',
  md: 'text-lais-heading-lg',
  sm: 'text-lais-heading-md',
});

const CONFIDENCE_TONES = Object.freeze({
  low: 'warning',
  medium: 'info',
  high: 'positive',
});

const CONFIDENCE_ICONS = Object.freeze({
  low: 'i-lucide-triangle-alert',
  medium: 'i-lucide-info',
  high: 'i-lucide-shield-check',
});

const { t } = useI18n();
const instance = getCurrentInstance();
const baseId = `lais-money-snapshot-${instance?.uid || 'default'}`;
const labelId = `${baseId}-label`;
const valueId = `${baseId}-value`;
const helperId = `${baseId}-helper`;
const rateId = `${baseId}-rate`;
const provenanceId = `${baseId}-provenance`;

const formatCurrency = value => {
  if (value === null || value === undefined || value === '') return '—';
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) return '—';
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: props.currency || 'BRL',
    }).format(parsed);
  } catch {
    return `${new Intl.NumberFormat('pt-BR').format(parsed)} ${props.currency}`;
  }
};

const formatDate = value => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return typeof value === 'string' ? value : '';
  }
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

const numericAmount = computed(() => {
  const { amount } = props;
  if (amount === null || amount === undefined || amount === '') return null;
  const parsed = typeof amount === 'number' ? amount : Number(amount);
  return Number.isFinite(parsed) ? parsed : null;
});
const hasAmount = computed(() => numericAmount.value !== null);
const formattedAmount = computed(() => formatCurrency(numericAmount.value));

const resolvedTone = computed(() => {
  if (!hasAmount.value) return 'neutral';
  return TONE_ALIASES[String(props.tone || '').toLowerCase()] || 'neutral';
});
const valueToneClass = computed(() => VALUE_TONE_CLASSES[resolvedTone.value]);
const emphasisClass = computed(
  () => EMPHASIS_CLASSES[props.emphasis] || EMPHASIS_CLASSES.md
);

const snapshotLabel = computed(() => formatDate(props.snapshotAt));
const hasProvenance = computed(
  () => Boolean(props.source) || Boolean(snapshotLabel.value)
);
const provenanceText = computed(() => {
  const parts = [];
  if (props.source) {
    parts.push(
      t('LAIS_MONEY_SNAPSHOT.PROVENANCE.SOURCE', { source: props.source })
    );
  }
  if (snapshotLabel.value) {
    parts.push(
      t('LAIS_MONEY_SNAPSHOT.PROVENANCE.SNAPSHOT', {
        date: snapshotLabel.value,
      })
    );
  }
  return parts.join(' · ');
});
const noProvenanceLabel = computed(() =>
  t('LAIS_MONEY_SNAPSHOT.PROVENANCE.NONE')
);

const normalizedConfidence = computed(() => {
  const { confidence } = props;
  if (confidence === '' || confidence === null || confidence === undefined) {
    return '';
  }
  if (typeof confidence === 'number') {
    if (confidence < 0.34) return 'low';
    if (confidence < 0.67) return 'medium';
    return 'high';
  }
  const value = String(confidence).toLowerCase();
  return ['low', 'medium', 'high'].includes(value) ? value : '';
});
const hasConfidence = computed(() => normalizedConfidence.value !== '');
const confidenceTone = computed(
  () => CONFIDENCE_TONES[normalizedConfidence.value] || 'neutral'
);
const confidenceIcon = computed(
  () => CONFIDENCE_ICONS[normalizedConfidence.value] || ''
);
const confidenceLabel = computed(() => {
  if (normalizedConfidence.value === 'low') {
    return t('LAIS_MONEY_SNAPSHOT.CONFIDENCE.LOW');
  }
  if (normalizedConfidence.value === 'medium') {
    return t('LAIS_MONEY_SNAPSHOT.CONFIDENCE.MEDIUM');
  }
  if (normalizedConfidence.value === 'high') {
    return t('LAIS_MONEY_SNAPSHOT.CONFIDENCE.HIGH');
  }
  return '';
});

const missingAmountText = computed(() =>
  t('LAIS_MONEY_SNAPSHOT.MISSING_AMOUNT')
);

const rateText = computed(() => {
  if (!props.rate || !props.rate.from || !props.rate.to) return '';
  const parsed = Number(props.rate.value);
  const value = Number.isFinite(parsed)
    ? new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }).format(parsed)
    : props.rate.value;
  return t('LAIS_MONEY_SNAPSHOT.RATE', {
    from: props.rate.from,
    to: props.rate.to,
    value,
  });
});

const historyItems = computed(() =>
  (props.history || [])
    .filter(item => item && item.amount !== undefined && item.amount !== null)
    .map((item, index) => ({
      key: `${baseId}-history-${index}`,
      dateLabel: formatDate(item.at),
      amountLabel: formatCurrency(item.amount),
    }))
);
const hasHistory = computed(() => historyItems.value.length > 0);
const historyTitle = computed(() => t('LAIS_MONEY_SNAPSHOT.HISTORY.TITLE'));

const groupLabelledBy = computed(() => {
  const ids = [];
  if (props.label) ids.push(labelId);
  ids.push(valueId);
  return ids.join(' ');
});
const groupDescribedBy = computed(() => {
  const ids = [];
  if (!hasAmount.value) ids.push(helperId);
  if (rateText.value) ids.push(rateId);
  if (hasProvenance.value) ids.push(provenanceId);
  return ids.length ? ids.join(' ') : undefined;
});

const copyLabel = computed(() =>
  t('LAIS_MONEY_SNAPSHOT.COPY', { value: formattedAmount.value })
);
const handleCopy = () => {
  if (!hasAmount.value) return;
  emit('copy', {
    amount: numericAmount.value,
    currency: props.currency || 'BRL',
    formatted: formattedAmount.value,
  });
};
</script>

<template>
  <div
    role="group"
    :aria-labelledby="groupLabelledBy"
    :aria-describedby="groupDescribedBy"
    class="flex flex-col gap-3 rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 shadow-lais-card"
  >
    <p
      v-if="label"
      :id="labelId"
      class="text-lais-label-sm font-semibold uppercase tracking-[0.04em] text-lais-text-body"
    >
      {{ label }}
    </p>

    <div class="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span
        :id="valueId"
        class="font-redHatDisplay font-semibold leading-none"
        :class="[emphasisClass, valueToneClass]"
      >
        {{ formattedAmount }}
      </span>
      <span
        v-if="unit && hasAmount"
        class="text-lais-body-sm font-medium text-lais-text-muted"
      >
        {{ unit }}
      </span>
      <button
        v-if="copyable && hasAmount"
        type="button"
        class="ml-auto grid size-8 shrink-0 place-items-center rounded-full text-lais-text-muted transition-colors hover:bg-lais-primary-50 hover:text-lais-primary focus-visible:outline-none focus-visible:shadow-lais-focus"
        :aria-label="copyLabel"
        @click="handleCopy"
      >
        <span class="i-lucide-copy size-4" aria-hidden="true" />
      </button>
    </div>

    <p
      v-if="!hasAmount"
      :id="helperId"
      class="text-lais-body-sm text-lais-text-helper"
    >
      {{ missingAmountText }}
    </p>

    <p
      v-if="rateText"
      :id="rateId"
      class="inline-flex items-center gap-1.5 text-lais-body-xs text-lais-text-muted"
    >
      <span
        class="i-lucide-arrow-left-right size-3.5 shrink-0"
        aria-hidden="true"
      />
      <span class="min-w-0 truncate">{{ rateText }}</span>
    </p>

    <div class="flex flex-wrap items-center gap-2">
      <p
        v-if="hasProvenance"
        :id="provenanceId"
        class="inline-flex min-w-0 items-center gap-1.5 text-lais-body-xs text-lais-text-helper"
      >
        <span class="i-lucide-database size-3.5 shrink-0" aria-hidden="true" />
        <span class="truncate">{{ provenanceText }}</span>
      </p>
      <LaisStatusBadge
        v-else
        tone="neutral"
        size="sm"
        icon="i-lucide-shield-alert"
        :label="noProvenanceLabel"
      />
      <LaisStatusBadge
        v-if="hasConfidence"
        :tone="confidenceTone"
        size="sm"
        :icon="confidenceIcon"
        :label="confidenceLabel"
      />
    </div>

    <div v-if="hasHistory" class="border-t border-lais-border-soft pt-3">
      <p
        class="mb-2 text-lais-label-xs font-semibold uppercase tracking-[0.04em] text-lais-text-muted"
      >
        {{ historyTitle }}
      </p>
      <ul class="flex flex-col gap-1.5">
        <li
          v-for="item in historyItems"
          :key="item.key"
          class="flex items-center justify-between gap-3 text-lais-body-xs"
        >
          <span class="text-lais-text-muted">{{ item.dateLabel }}</span>
          <span class="font-medium text-lais-text-body">{{
            item.amountLabel
          }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>
